import { c as _c } from "react-compiler-runtime";
// Recording card that reuses MediaPlayer and Transcript from
// recording-review-components-lib, validating they work with just a
// presigned URL and safe prop values, no auth context

import { useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Box } from 'foundations-components/transitional/components/Box';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardWrapper, CardSection } from 'foundations-components/transitional/components/Card';
import { MediaPlayerProvider } from 'recording-review-components-lib/media-player/variant-default/MediaPlayerProvider';
import { ADVANCED } from 'recording-review-components-lib/types/MediaTimelineTypes';
import { VIDEO, AUDIO } from 'recording-review-components-lib/types/CallMediaTypes';
import { composeMediaElement } from 'recording-review-components-lib/media-player/hoc/composeMediaElement';
import VolumeControl from 'recording-review-components-lib/media-player/components/MediaPlayerControls/VolumeControl';
import RewindBtn from 'recording-review-components-lib/media-player/components/MediaPlayerControls/RewindBtn';
import PlayPauseBtn from 'recording-review-components-lib/media-player/components/MediaPlayerControls/PlayPauseBtn';
import ForwardBtn from 'recording-review-components-lib/media-player/components/MediaPlayerControls/ForwardBtn';
import SpeedSelect from 'recording-review-components-lib/media-player/components/MediaPlayerControls/SpeedSelect';
import { MediaTimeline } from 'recording-review-components-lib/media-player/variant-default/MediaTimeline';
import { TranscriptContextProvider } from 'recording-review-components-lib/contexts/TranscriptContext';
import { Transcript } from 'recording-review-components-lib/components/transcript/Transcript';
import { toDetailedSpeakers, toTranscriptUtterances, toTimelineUtterances, toTranscriptParticipants } from '../utils/transcriptTransforms';
import { useIsMobile } from '../hooks/useIsMobile';

// media controls handle playback internally via MediaPlayerProvider context,
// these callbacks are optional tracking hooks that fire after the action
// (the authenticated app uses them for Amplitude events, we skip tracking here)
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const trackingNoop = () => {};
const VIDEO_PLAYER_OPTIONS_DESKTOP = {
  maxHeight: 350
};
const VIDEO_PLAYER_OPTIONS_MOBILE = {
  maxHeight: 240
};
const MediaElement = composeMediaElement(['INTEGRATIONS_RECORDING_REFRESH', 'INTERNAL_RECORDING_URL_PARSER']);
export const PublicRecordingCard = t0 => {
  const $ = _c(40);
  const {
    recording,
    transcript,
    hasVideo,
    hasAudio
  } = t0;
  const isMobile = useIsMobile();
  const [showTranscript, setShowTranscript] = useState(false);
  const hasTranscriptData = transcript.utterances.length > 0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setShowTranscript(_temp);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleToggleTranscript = t1;
  let t2;
  if ($[1] !== transcript) {
    t2 = toDetailedSpeakers(transcript);
    $[1] = transcript;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const detailedSpeakers = t2;
  let t3;
  if ($[3] !== transcript) {
    t3 = toTranscriptUtterances(transcript);
    $[3] = transcript;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const transcriptUtterances = t3;
  let t4;
  if ($[5] !== transcript) {
    t4 = toTimelineUtterances(transcript);
    $[5] = transcript;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const timelineUtterances = t4;
  let t5;
  if ($[7] !== transcript) {
    t5 = toTranscriptParticipants(transcript);
    $[7] = transcript;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const transcriptParticipants = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = /*#__PURE__*/_jsx(Text, {
      typography: "heading-200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "external-sharing-components.recordingCard.heading"
      })
    });
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== hasTranscriptData || $[11] !== showTranscript) {
    t7 = hasTranscriptData && /*#__PURE__*/_jsx(Button, {
      use: "secondary",
      size: "extra-small",
      onClick: handleToggleTranscript,
      children: showTranscript ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "external-sharing-components.recordingCard.hideTranscript"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "external-sharing-components.recordingCard.showTranscript"
      })
    });
    $[10] = hasTranscriptData;
    $[11] = showTranscript;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = /*#__PURE__*/_jsxs(Flex, {
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "200",
      marginBlockEnd: "200",
      children: [t6, t7]
    });
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = hasVideo ? VIDEO : AUDIO;
  const t10 = isMobile ? VIDEO_PLAYER_OPTIONS_MOBILE : VIDEO_PLAYER_OPTIONS_DESKTOP;
  let t11;
  if ($[15] !== t10) {
    t11 = /*#__PURE__*/_jsx(View, {
      as: "div",
      paddingBlockEnd: "200",
      children: /*#__PURE__*/_jsx(MediaElement, {
        videoPlayerOptions: t10
      })
    });
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== hasAudio || $[18] !== hasVideo || $[19] !== isMobile) {
    t12 = (hasVideo || hasAudio) && (isMobile ? /*#__PURE__*/_jsxs(Flex, {
      "data-test-id": "mediaPlayer",
      justifyContent: "center",
      alignItems: "center",
      columnGap: "500",
      children: [/*#__PURE__*/_jsx(RewindBtn, {
        onRewindCallback: trackingNoop
      }), /*#__PURE__*/_jsx(PlayPauseBtn, {
        onPlayCallback: trackingNoop,
        onPauseCallback: trackingNoop
      }), /*#__PURE__*/_jsx(ForwardBtn, {
        onForwardCallback: trackingNoop
      })]
    }) : /*#__PURE__*/_jsxs(Flex, {
      "data-test-id": "mediaPlayer",
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(Box, {
        basis: "33.3%",
        children: /*#__PURE__*/_jsx(VolumeControl, {
          onVolumeChangeCallback: trackingNoop
        })
      }), /*#__PURE__*/_jsx(Box, {
        basis: "33.3%",
        children: /*#__PURE__*/_jsxs(Flex, {
          justifyContent: "center",
          alignItems: "center",
          columnGap: "500",
          children: [/*#__PURE__*/_jsx(RewindBtn, {
            onRewindCallback: trackingNoop
          }), /*#__PURE__*/_jsx(PlayPauseBtn, {
            onPlayCallback: trackingNoop,
            onPauseCallback: trackingNoop
          }), /*#__PURE__*/_jsx(ForwardBtn, {
            onForwardCallback: trackingNoop
          })]
        })
      }), /*#__PURE__*/_jsx(Box, {
        basis: "33.3%",
        children: /*#__PURE__*/_jsx(Flex, {
          justifyContent: "end",
          children: /*#__PURE__*/_jsx(SpeedSelect, {
            onSpeedSelectCallback: trackingNoop
          })
        })
      })]
    }));
    $[17] = hasAudio;
    $[18] = hasVideo;
    $[19] = isMobile;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== detailedSpeakers || $[22] !== timelineUtterances) {
    t13 = /*#__PURE__*/_jsx(View, {
      as: "div",
      paddingBlockStart: "200",
      children: /*#__PURE__*/_jsx(MediaTimeline, {
        variant: ADVANCED,
        showCallTopics: false,
        transcriptUtterances: timelineUtterances,
        detailedSpeakers: detailedSpeakers
      })
    });
    $[21] = detailedSpeakers;
    $[22] = timelineUtterances;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== hasTranscriptData || $[25] !== isMobile || $[26] !== showTranscript || $[27] !== transcriptParticipants || $[28] !== transcriptUtterances) {
    t14 = showTranscript && hasTranscriptData && /*#__PURE__*/_jsx(View, {
      as: "div",
      paddingBlockStart: "400",
      paddingInlineEnd: "200",
      maxBlockSize: isMobile ? "300px" : "460px",
      overflowY: "auto",
      children: /*#__PURE__*/_jsx(TranscriptContextProvider, {
        transcriptUtterances: transcriptUtterances,
        transcriptParticipants: transcriptParticipants,
        callSource: "INTEGRATIONS_PLATFORM",
        assignedUser: null,
        hasSalesStarterSeat: true,
        hasServiceStarterSeat: true,
        hasTranscriptionFeature: true,
        isTranscriptionAdmin: false,
        callOwnerHasTranscriptionScope: true,
        transcriptionStatus: "COMPLETED",
        isTranscriptUsageLimitReached: false,
        onTranscriptSearchCallback: trackingNoop,
        onTranscriptAutoScrollClickCallback: trackingNoop,
        onMapSpeaker: trackingNoop,
        onUnmapSpeaker: trackingNoop,
        children: /*#__PURE__*/_jsx(Transcript, {
          variant: "BASIC",
          callId: "",
          onShareIconClick: undefined,
          onCommentIconClick: undefined,
          onCopyIconClick: trackingNoop,
          onTranscriptFeedback: trackingNoop
        })
      })
    });
    $[24] = hasTranscriptData;
    $[25] = isMobile;
    $[26] = showTranscript;
    $[27] = transcriptParticipants;
    $[28] = transcriptUtterances;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== recording.presignedUrl || $[31] !== t11 || $[32] !== t12 || $[33] !== t13 || $[34] !== t14 || $[35] !== t9) {
    t15 = /*#__PURE__*/_jsxs(MediaPlayerProvider, {
      recordingUrl: recording.presignedUrl,
      mediaType: t9,
      initialClipStartTime: 0,
      children: [t11, t12, t13, t14]
    });
    $[30] = recording.presignedUrl;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t9;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t15 || $[38] !== t8) {
    t16 = /*#__PURE__*/_jsx(CardWrapper, {
      children: /*#__PURE__*/_jsxs(CardSection, {
        children: [t8, t15]
      })
    });
    $[37] = t15;
    $[38] = t8;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
};
function _temp(prev) {
  return !prev;
}